import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Bars3Icon, XMarkIcon, PowerIcon, HomeIcon, ShoppingBagIcon, CogIcon } from '@heroicons/react/24/outline'

const menuItems = [
  { label: 'Home', path: '/admin', icon: HomeIcon },
  { label: 'Productos', path: '/admin-productos', icon: ShoppingBagIcon },
  { label: 'Usuarios', path: '/admin-usuarios', icon: CogIcon, adminOnly: true },
]

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const role = localStorage.getItem('adminRole') || 'admin'

  const handleLogout = () => {
    localStorage.removeItem('adminAuth')
    localStorage.removeItem('adminLoginTime')
    localStorage.removeItem('adminEmail')
    localStorage.removeItem('adminRole')
    navigate('/admin-login')
  }

  const isActive = (path) => location.pathname === path

  return (
    <div className="flex h-screen bg-gray-950">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-72' : 'w-24'} bg-gradient-to-b from-gray-900 to-gray-950 border-r border-white/5 transition-all duration-300 flex flex-col overflow-hidden shadow-2xl`}>
        {/* Logo */}
        <div className={`flex items-center justify-between px-4 py-6 border-b border-white/10 ${!sidebarOpen && 'justify-center'}`}>
          <div className={`flex items-center gap-3 ${!sidebarOpen && 'hidden'}`}>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-adrox-blue to-cyan-500 flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <div>
              <p className="text-white font-bold text-sm">Adrox Admin</p>
              <p className="text-gray-400 text-xs">Gestión de catálogo</p>
            </div>
          </div>
          {sidebarOpen && (
            <button onClick={() => setSidebarOpen(false)} className="text-gray-400 hover:text-white transition-colors">
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
          {!sidebarOpen && (
            <button onClick={() => setSidebarOpen(true)} className="text-gray-400 hover:text-white transition-colors">
              <Bars3Icon className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Search (solo cuando está abierto) */}
        {sidebarOpen && (
          <div className="px-4 py-4 border-b border-white/10">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar..."
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-adrox-blue/50 text-sm"
              />
            </div>
          </div>
        )}

        {/* Menu Items */}
        <nav className="flex-1 space-y-1 p-4 overflow-y-auto">
          {menuItems
            .filter(item => !item.adminOnly || role === 'admin')
            .map(item => {
              const Icon = item.icon
              const active = isActive(item.path)
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-4 px-4 py-3 rounded-lg transition-all duration-200 group ${
                    active
                      ? 'bg-gradient-to-r from-adrox-blue/30 to-cyan-500/30 text-adrox-blue border border-adrox-blue/30 shadow-lg shadow-adrox-blue/20'
                      : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 transition-colors ${active ? 'text-adrox-blue' : 'group-hover:text-cyan-400'}`} />
                  <span className={`text-sm font-medium ${sidebarOpen ? 'inline' : 'hidden'}`}>{item.label}</span>
                </button>
              )
            })}
        </nav>

        {/* Logout */}
        <div className={`border-t border-white/10 p-4 ${!sidebarOpen && 'flex justify-center'}`}>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center justify-center gap-2 bg-red-500/15 hover:bg-red-500/25 text-red-400 hover:text-red-300 px-4 py-3 rounded-lg transition-all border border-red-500/20 hover:border-red-500/40 ${!sidebarOpen && 'w-auto p-3'}`}
            title="Cerrar Sesión"
          >
            <PowerIcon className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm font-medium">Cerrar Sesión</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Navbar */}
        <header className="bg-gradient-to-r from-gray-900 via-gray-900 to-gray-950 border-b border-white/5 px-8 py-5 flex items-center justify-between shadow-lg">
          <div>
            <h1 className="text-2xl font-bold text-white">Panel Administrativo</h1>
            <p className="text-gray-400 text-sm">Gestión de productos y usuarios · Adrox Peru</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-white text-sm font-medium">{localStorage.getItem('adminEmail')}</p>
              <p className="text-gray-400 text-xs capitalize">Rol: <span className="text-adrox-blue font-semibold">{role}</span></p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-adrox-blue to-cyan-500 flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-sm">{localStorage.getItem('adminEmail')?.charAt(0).toUpperCase()}</span>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto bg-gray-950">{children}</main>
      </div>
    </div>
  )
}
